import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { EmpresaService } from '@modules/empresa/services/empresa.service';
import { urlDoArquivo } from '../../utils/url-arquivo';

/** Extrai até 2 iniciais maiúsculas do nome da empresa. Ex.: "Frigosul SIF" → "FS"; "Frigosul" → "FR". */
function extrairIniciais(nome: string): string {
  const palavras = nome.trim().split(/\s+/).filter(Boolean);
  if (palavras.length === 0) return '?';
  if (palavras.length === 1) {
    return palavras[0].substring(0, 2).toUpperCase();
  }
  return (palavras[0][0] + palavras[1][0]).toUpperCase();
}

@Component({
  selector: 'app-logo-empresa',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (urlResolvida()) {
      <img [src]="urlResolvida()" [alt]="nome()" [class]="classesTamanho()" class="rounded object-contain" />
    } @else {
      <span
        [class]="classesTamanho()"
        class="rounded flex items-center justify-center bg-color-brand/10 text-color-brand font-semibold select-none"
        [attr.aria-label]="'Logo de ' + nome()"
      >
        {{ iniciais() }}
      </span>
    }
  `,
})
export class LogoEmpresaComponent implements OnInit {
  private readonly _empresaService = inject(EmpresaService);

  public readonly nome = input.required<string>();
  public readonly logoAnexoId = input<string | null | undefined>(null);
  public readonly logoUrl = input<string | null | undefined>(null);
  public readonly tamanho = input<'sm' | 'md'>('md');
  /**
   * O logo é da empresa em que o usuário está agora. Só esse pode ser buscado: `GET /empresa/logo`
   * responde pelo tenant do token. Os anexos de outras empresas (lista de troca de empresa) não são
   * legíveis daqui, e mostram as iniciais.
   *
   * Não usar `GET /anexos/:id`: ele exige a permissão de preenchimentos, e o 403 mandava o fiscal
   * (#733) e qualquer usuário sem ela para /acesso-negado em todas as telas.
   */
  public readonly daEmpresaAtual = input(false, { transform: booleanAttribute });

  protected readonly urlResolvida = signal<string | null>(null);

  protected readonly iniciais = computed(() => extrairIniciais(this.nome()));

  protected readonly classesTamanho = computed(() => (this.tamanho() === 'sm' ? 'h-7 w-7' : 'h-10 w-10'));

  ngOnInit(): void {
    const anexoId = this.logoAnexoId();
    const url = this.logoUrl();

    if (anexoId) {
      if (!this.daEmpresaAtual()) return;
      this._empresaService.obterLogo().subscribe({
        next: (logo) => this.urlResolvida.set(urlDoArquivo(logo.url)),
        error: () => this.urlResolvida.set(null),
      });
    } else if (url) {
      this.urlResolvida.set(urlDoArquivo(url));
    }
  }
}
